/**
 * AcademyIntroPlayer — premium custom HTML5 player for the official
 * Academy Introduction film. Self-hosted; visitors never leave
 * StoicOs.ai. Black & gold, rounded corners, elegant shadow.
 * Lazy: the <video> element mounts only when scrolled near viewport,
 * and then preloads metadata only. Poster = the official Hero artwork.
 */
import { useEffect, useRef, useState } from "react";
import {
  Play, Pause, Volume2, VolumeX, Maximize, Minimize,
  PictureInPicture2, Gauge,
} from "lucide-react";

const SPEEDS = [0.75, 1, 1.25, 1.5];

function fmt(s) {
  if (!Number.isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${String(r).padStart(2, "0")}`;
}

export const AcademyIntroPlayer = ({ src, poster }) => {
  const wrapRef = useRef(null);
  const videoRef = useRef(null);
  const [near, setNear] = useState(false);        // lazy mount
  const [playing, setPlaying] = useState(false);
  const [started, setStarted] = useState(false);
  const [ended, setEnded] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speedIdx, setSpeedIdx] = useState(1);
  const [fullscreen, setFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const hideTimer = useRef(null);

  // Lazy-load: mount the video element only when the player approaches
  // the viewport, then let the browser preload metadata only.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setNear(true); io.disconnect(); }
    }, { rootMargin: "400px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const onFs = () => setFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  const poke = () => {
    setShowControls(true);
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => {
      if (videoRef.current && !videoRef.current.paused) setShowControls(false);
    }, 2600);
  };

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play().catch(() => {}); } else { v.pause(); }
  };

  const seek = (e) => {
    const v = videoRef.current;
    if (!v || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    v.currentTime = Math.min(Math.max(x / rect.width, 0), 1) * duration;
  };

  const cycleSpeed = () => {
    const next = (speedIdx + 1) % SPEEDS.length;
    setSpeedIdx(next);
    if (videoRef.current) videoRef.current.playbackRate = SPEEDS[next];
  };

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else wrapRef.current?.requestFullscreen?.();
  };

  const togglePiP = async () => {
    const v = videoRef.current;
    if (!v) return;
    try {
      if (document.pictureInPictureElement) await document.exitPictureInPicture();
      else await v.requestPictureInPicture();
    } catch { /* PiP unsupported — button is hidden in that case */ }
  };

  const onVolume = (e) => {
    const val = Number(e.target.value);
    setVolume(val);
    setMuted(val === 0);
    if (videoRef.current) { videoRef.current.volume = val; videoRef.current.muted = val === 0; }
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  const pipSupported = typeof document !== "undefined" && "pictureInPictureEnabled" in document && document.pictureInPictureEnabled;
  const progress = duration ? (time / duration) * 100 : 0;

  return (
    <div
      ref={wrapRef}
      data-testid="academy-intro-player"
      onMouseMove={poke}
      onTouchStart={poke}
      className="group relative aspect-video w-full rounded-xl md:rounded-2xl overflow-hidden bg-black
        border border-gold/30 shadow-[0_30px_90px_-20px_rgba(232,200,121,0.22)]"
    >
      {near && (
        <video
          ref={videoRef}
          src={src}
          poster={poster}
          preload="metadata"
          playsInline
          onClick={toggle}
          onPlay={() => { setPlaying(true); setStarted(true); setEnded(false); poke(); }}
          onPause={() => { setPlaying(false); setShowControls(true); }}
          onEnded={() => { setEnded(true); setPlaying(false); setShowControls(true); }}
          onTimeUpdate={(e) => setTime(e.target.currentTime)}
          onLoadedMetadata={(e) => setDuration(e.target.duration)}
          className="absolute inset-0 w-full h-full object-contain bg-black cursor-pointer"
        />
      )}

      {/* Quiet gold atmosphere before first play */}
      {!started && (
        <div aria-hidden className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(60% 60% at 50% 45%, rgba(232,200,121,0.07) 0%, rgba(0,0,0,0.35) 100%)" }} />
      )}

      {/* Ended — gentle fade back toward the poster, inviting the journey onward */}
      {ended && (
        <div aria-hidden className="absolute inset-0 bg-black/60 transition-opacity duration-1000 pointer-events-none" />
      )}

      {/* Center play — shown before playback and when paused */}
      {!playing && (
        <button
          type="button"
          onClick={toggle}
          data-testid="academy-intro-play-button"
          aria-label="Play the Academy Introduction"
          className="absolute inset-0 flex items-center justify-center"
        >
          <span className="w-16 h-16 md:w-20 md:h-20 rounded-full border border-gold/70 bg-black/40
            backdrop-blur-sm flex items-center justify-center transition-transform duration-500
            hover:scale-105 shadow-[0_0_50px_-8px_rgba(232,200,121,0.5)]">
            <Play size={26} strokeWidth={1.25} className="text-gold ml-1" aria-hidden />
          </span>
        </button>
      )}

      {/* Control bar */}
      {near && (
        <div
          data-testid="academy-intro-controls"
          className={`absolute inset-x-0 bottom-0 px-3 md:px-5 pb-2.5 md:pb-3.5 pt-10
            bg-gradient-to-t from-black/85 via-black/45 to-transparent
            transition-opacity duration-500 ${showControls || !playing ? "opacity-100" : "opacity-0"}`}
        >
          {/* Progress */}
          <div
            role="slider"
            aria-label="Video progress"
            aria-valuemin={0}
            aria-valuemax={Math.round(duration)}
            aria-valuenow={Math.round(time)}
            tabIndex={0}
            onClick={seek}
            data-testid="academy-intro-progress"
            className="relative h-4 flex items-center cursor-pointer"
          >
            <div className="w-full h-[3px] bg-white/20 rounded-full overflow-hidden">
              <div className="h-full bg-gold rounded-full" style={{ width: `${progress}%` }} />
            </div>
            <div
              className="absolute w-3 h-3 rounded-full bg-gold shadow -translate-x-1/2"
              style={{ left: `${progress}%` }}
              aria-hidden
            />
          </div>

          <div className="mt-1 flex items-center gap-3 md:gap-4">
            <button type="button" onClick={toggle} data-testid="academy-intro-toggle"
              aria-label={playing ? "Pause" : "Play"}
              className="text-bone hover:text-gold transition-colors">
              {playing ? <Pause size={18} strokeWidth={1.5} /> : <Play size={18} strokeWidth={1.5} />}
            </button>

            <div className="flex items-center gap-2">
              <button type="button" onClick={toggleMute} data-testid="academy-intro-mute"
                aria-label={muted ? "Unmute" : "Mute"}
                className="text-bone hover:text-gold transition-colors">
                {muted ? <VolumeX size={17} strokeWidth={1.5} /> : <Volume2 size={17} strokeWidth={1.5} />}
              </button>
              <input
                type="range" min="0" max="1" step="0.05"
                value={muted ? 0 : volume}
                onChange={onVolume}
                aria-label="Volume"
                data-testid="academy-intro-volume"
                className="hidden sm:block w-16 md:w-20 accent-[#e8c879] cursor-pointer"
              />
            </div>

            <span className="text-[11px] tracking-wider text-bone/70 tabular-nums" data-testid="academy-intro-time">
              {fmt(time)} <span className="text-bone/35">/</span> {fmt(duration)}
            </span>

            <div className="flex-1" />

            <button type="button" onClick={cycleSpeed} data-testid="academy-intro-speed"
              aria-label="Playback speed"
              className="flex items-center gap-1 text-[11px] tracking-wider text-bone/80 hover:text-gold transition-colors">
              <Gauge size={15} strokeWidth={1.5} />
              {SPEEDS[speedIdx]}×
            </button>

            {pipSupported && (
              <button type="button" onClick={togglePiP} data-testid="academy-intro-pip"
                aria-label="Picture in picture"
                className="text-bone/80 hover:text-gold transition-colors">
                <PictureInPicture2 size={16} strokeWidth={1.5} />
              </button>
            )}

            <button type="button" onClick={toggleFullscreen} data-testid="academy-intro-fullscreen"
              aria-label={fullscreen ? "Exit fullscreen" : "Fullscreen"}
              className="text-bone/80 hover:text-gold transition-colors">
              {fullscreen ? <Minimize size={17} strokeWidth={1.5} /> : <Maximize size={17} strokeWidth={1.5} />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
